import type { ClientStreamStatsMessage, MeasurementFreshness, ResolutionChangeRequest, ResolutionChangeStatus } from '@playcastdotio/PeerCore';
import { ReportFreshnessGate } from '../../../../../../../../libs/PeerCore/src/integration/ReportFreshnessGate';
import { MobileResolutionEnforcer } from './MobileResolutionEnforcer';
import { ResolutionChangeVerifier } from './ResolutionChangeVerifier';
import type { QualityTierDemotionContext } from '../RecoveryCapacityGate';
import { RESOLUTION_HOST_VERIFY_MS } from '../../../../../../../../libs/PeerCore/src/streamRecovery/policy';

type Dimensions = { width: number; height: number };
type SenderObservation = Dimensions & { frames: number; streamId: string; pollStartedAtMs: number };
type PeerState = {
  freshness: ReportFreshnessGate; epoch: number; sequence: number;
  request?: ResolutionChangeRequest; enforcer?: MobileResolutionEnforcer; task?: Promise<void>;
  status?: ResolutionChangeStatus; initial?: Dimensions; sender?: SenderObservation;
  baseline?: SenderObservation; receiver?: Dimensions & { atMs: number; deviceClass?: 'mobile' | 'desktop' | 'unknown' };
  senderElapsed?: number; receiverElapsed?: number;
};

/** Per-connection verification lifecycle; retained caps survive a reconnect. */
export class PeerResolutionManager {
  private readonly peers = new Map<string, PeerState>();
  private readonly retainedFloors = new Set<string>();
  private generation = 0;

  constructor(private readonly deps: {
    nowMs: () => number;
    guestWallTimeBounds: (peerId: string, guestWallMs: number, epoch: number) => { earliestMs: number; latestMs: number } | undefined;
    emit: (peerId: string, request: ResolutionChangeRequest, status: ResolutionChangeStatus) => void;
    onSenderVerified: (peerId: string) => void;
  }) {}

  public hasRetainedFloor(peerId: string): boolean { return this.retainedFloors.has(peerId); }
  public status(peerId: string): ResolutionChangeStatus | undefined { return this.peers.get(peerId)?.status; }
  public senderDimensions(peerId: string): Dimensions | undefined { return this.peers.get(peerId)?.sender; }
  public observePath(peerId: string, epoch: number): void { this.state(peerId).epoch = Math.max(epoch, this.state(peerId).epoch); }

  public request(peerId: string, apply: (context: QualityTierDemotionContext & { requestId: string; hostDeadlineMs: number }) => Promise<void>): Promise<void> {
    const state = this.state(peerId);
    if (state.task) return state.task;
    if (state.status && !['failed', 'host-timeout', 'superseded'].includes(state.status.stage)) return Promise.resolve();
    state.enforcer?.dispose();
    const request: ResolutionChangeRequest = { requestId: `${peerId}:${++this.generation}`,
      peerGeneration: this.generation, pathEpoch: state.epoch, maxWidth: 640, maxHeight: 360,
      requestedAtMs: this.deps.nowMs(), reason: 'sustained-poor-playback' };
    state.request = request;
    state.baseline = undefined;
    state.senderElapsed = state.receiverElapsed = undefined;
    this.retainedFloors.add(peerId);
    const isCurrent = () => this.peers.get(peerId) === state && state.request === request;
    const enforcer = new MobileResolutionEnforcer({ verifier: new ResolutionChangeVerifier(), nowMs: this.deps.nowMs,
      effects: { apply: () => apply({ isCurrent, requestId: request.requestId,
        hostDeadlineMs: request.requestedAtMs + RESOLUTION_HOST_VERIFY_MS }), readSenderDimensions: () => state.sender },
      emit: status => {
        if (!isCurrent()) return;
        state.status = status;
        if (status.stage === 'sender-verified') {
          state.senderElapsed = status.elapsedMs;
          this.deps.onSenderVerified(peerId);
        }
        if (status.stage === 'receiver-verified') state.receiverElapsed = status.elapsedMs - (state.senderElapsed ?? 0);
        this.deps.emit(peerId, request, status);
      } });
    state.enforcer = enforcer;
    const task = enforcer.request(request);
    state.task = task;
    const release = () => { if (state.task === task) state.task = undefined; };
    // Drop settled promises, including failures: a later failure episode must be retryable.
    void task.then(release, release);
    return task;
  }

  public observeSender(peerId: string, sample: SenderObservation): void {
    const state = this.state(peerId);
    if (![sample.frames, sample.width, sample.height, sample.pollStartedAtMs].every(Number.isFinite)
      || sample.frames < 0 || sample.width <= 0 || sample.height <= 0) return;
    if (state.sender && sample.pollStartedAtMs < state.sender.pollStartedAtMs) return;
    state.sender = sample;
    if (!state.initial && sample.frames > 0) state.initial = { width: sample.width, height: sample.height };
    const request = state.request;
    if (!request || sample.pollStartedAtMs < request.requestedAtMs) return;
    const baseline = state.baseline;
    state.baseline = sample;
    // First post-request poll establishes this RTP stream's baseline. A reset is not progress.
    if (!baseline || baseline.streamId !== sample.streamId || sample.frames <= baseline.frames || sample.frames <= 0) return;
    state.enforcer?.observe({ peerGeneration: request.peerGeneration, pathEpoch: request.pathEpoch, source: 'sender',
      width: sample.width, height: sample.height, observationSequence: ++state.sequence,
      freshness: { state: 'fresh', ageMs: 0 }, observedAfterRequest: true });
  }

  public observeReceiver(peerId: string, msg: ClientStreamStatsMessage): MeasurementFreshness {
    const state = this.state(peerId);
    const epoch = msg.recoveryEvidence?.path?.epoch ?? 0;
    state.epoch = Math.max(epoch, state.epoch);
    const now = this.deps.nowMs();
    const bounds = msg.sampledAtEpochMs === undefined ? undefined : this.deps.guestWallTimeBounds(peerId, msg.sampledAtEpochMs, epoch);
    const transitAge = bounds && bounds.earliestMs <= now ? Math.max(0, now - bounds.earliestMs) : undefined;
    const accepted = state.freshness.accept({ epoch, sequence: msg.seq ?? 0, observationSequence: msg.seq,
      observationAgeMs: 0, receivedAtMs: now, producerTimestampMs: msg.sampledAtEpochMs, trustedTransitAgeMs: transitAge });
    const decoded = msg.receiverCounters?.framesDecoded;
    const progressed = decoded !== undefined && decoded > 0;
    if (accepted.state === 'fresh' && msg.frameWidth && msg.frameHeight && progressed) {
      state.receiver = { width: msg.frameWidth, height: msg.frameHeight, atMs: now - (accepted.ageMs ?? 0),
        deviceClass: msg.recoveryEvidence?.deviceClass };
    }
    const request = state.request;
    if (!request || msg.frameWidth === undefined || msg.frameHeight === undefined) return accepted;
    const intervalMs = msg.presentation?.elapsedMs;
    const afterRequest = !!bounds && transitAge !== undefined && intervalMs !== undefined && intervalMs > 0
      && bounds.earliestMs - intervalMs >= request.requestedAtMs;
    state.enforcer?.observe({ peerGeneration: request.peerGeneration, pathEpoch: epoch, source: 'receiver',
      width: msg.frameWidth, height: msg.frameHeight, observationSequence: msg.seq ?? 0,
      freshness: accepted, observedAfterRequest: progressed && afterRequest });
    return accepted;
  }

  public tick(peerId: string): void { this.peers.get(peerId)?.enforcer?.tick(); }

  public snapshot(peerId: string) {
    const state = this.peers.get(peerId);
    const stage = state?.status?.stage;
    const result: 'verified' | 'host-failed' | 'receiver-unverified' | 'condition-not-produced' =
      stage === 'receiver-verified' ? 'verified' : stage === 'failed' || stage === 'host-timeout' ? 'host-failed'
        : stage === 'sender-verified' || stage === 'receiver-unverified' ? 'receiver-unverified' : 'condition-not-produced';
    return { initialWidth: state?.initial?.width, initialHeight: state?.initial?.height,
      senderWidth: state?.sender?.width, senderHeight: state?.sender?.height,
      finalWidth: state?.receiver?.width, finalHeight: state?.receiver?.height,
      evidenceAgeMs: state?.receiver ? this.deps.nowMs() - state.receiver.atMs : undefined,
      deviceClass: state?.receiver?.deviceClass, hostVerificationMs: state?.senderElapsed,
      receiverConfirmationMs: state?.receiverElapsed, result };
  }

  public clearPeer(peerId: string, clearRetainedFloor = false): void {
    this.peers.get(peerId)?.enforcer?.dispose();
    this.peers.delete(peerId);
    if (clearRetainedFloor) this.retainedFloors.delete(peerId);
  }

  public clearAll(): void {
    for (const peerId of this.peers.keys()) this.clearPeer(peerId, true);
    this.retainedFloors.clear();
  }

  private state(peerId: string): PeerState {
    let state = this.peers.get(peerId);
    if (!state) { state = { freshness: new ReportFreshnessGate(), epoch: 0, sequence: 0 }; this.peers.set(peerId, state); }
    return state;
  }
}
